<template>
  <v-dialog :value="open" persistent max-width="1000px" :attach="true" @keydown.esc="close">
    <v-card style="padding: 0px">
      <v-card-title>
        Select Program Page
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <v-row class="mb-2" align="center">
          <v-col cols="4" class="py-1">
            <f-text v-model="titleFilter" label="Filter internal title" smlabel />
          </v-col>
          <v-col cols="3" class="py-1">
            <f-dropdown v-model="typeFilter" :items="typeFilterItems" label="Filter type" smlabel />
          </v-col>
          <v-col cols="5" class="py-1 d-flex justify-end align-center" style="gap: 8px">
            <g-button label="Refresh" icon="refresh" :loading="loading" @onclick="fetchPages" />
            <g-button label="Add page" icon="add" @onclick="goCreate" />
          </v-col>
        </v-row>

        <div class="overflow-y-auto" style="max-height: 480px">
          <v-simple-table dense>
            <thead>
              <tr>
                <th style="width: 40px"></th>
                  <th>Internal title</th>
                  <th>Type</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="page in filteredPages"
                  :key="page.id"
                  class="page-row"
                  :class="{ 'selected-page': selectedPageId === page.id }"
                  @click="selectedPageId = page.id"
                >
                  <td>
                    <v-icon small v-if="selectedPageId === page.id">mdi-radiobox-marked</v-icon>
                    <v-icon small v-else>mdi-radiobox-blank</v-icon>
                  </td>
                  <td>{{ formatInternalTitle(page) }}</td>
                  <td>{{ formatType(page.type) }}</td>
                  <td>{{ formatDate(page.updated_at) }}</td>
                </tr>
            </tbody>
          </v-simple-table>
          <div v-if="loading" class="text-center py-8 grey--text">Loading pages…</div>
          <div v-else-if="filteredPages.length === 0" class="text-center py-8 grey--text">
            No program pages found. Use “Add page”, then “Refresh” after creating.
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <g-button label="Cancel" @onclick="close" />
        <v-spacer></v-spacer>
        <g-button label="Select" type="primary" :disabled="!selectedPageId || selecting" :loading="selecting" @onclick="select" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { PAGE_TYPES, pageTypeLabel, pageInternalLabel } from "@/modules/program_pages";

export default {
  props: {
    value: Object,
  },
  data() {
    return {
      pages: [],
      loading: false,
      selecting: false,
      titleFilter: "",
      typeFilter: "",
      selectedPageId: null,
    };
  },
  computed: {
    open() {
      return this.value != null;
    },
    typeFilterItems() {
      return [{ label: "All types", value: "" }, ...PAGE_TYPES.map((item) => ({ label: item.label, value: item.value }))];
    },
    filteredPages() {
      const titleQuery = String(this.titleFilter || "")
        .trim()
        .toLowerCase();
      return (this.pages || []).filter((page) => {
        if (this.typeFilter && page.type !== this.typeFilter) return false;
        if (!titleQuery) return true;
        return pageInternalLabel(page).toLowerCase().includes(titleQuery);
      });
    },
  },
  methods: {
    close() {
      this.$emit("input", null);
    },
    formatInternalTitle(page) {
      return pageInternalLabel(page);
    },
    formatType(type) {
      return pageTypeLabel(type);
    },
    formatDate(value) {
      if (!value) return "—";
      try {
        return new Date(value).toLocaleDateString();
      } catch (e) {
        return String(value);
      }
    },
    goCreate() {
      const route = this.$router.resolve({ name: "program_pages_new" });
      window.open(route.href, "_blank", "noopener");
    },
    select() {
      if (!this.selectedPageId || this.selecting) return;
      this.selecting = true;
      this.$store
        .dispatch("ProgramPageStore/getProgramPagesByIds", [this.selectedPageId])
        .then((pages) => {
          const selectedPage = Array.isArray(pages) ? pages.find((page) => page.id === this.selectedPageId) : null;
          if (!selectedPage) {
            this.$root.$error("Page not found");
            return;
          }
          this.$emit("select", selectedPage);
          this.close();
        })
        .catch((err) => this.$root.$error(err))
        .finally(() => {
          this.selecting = false;
        });
    },
    fetchPages() {
      if (this.loading) return;
      const previousSelected = this.selectedPageId;
      this.loading = true;
      this.$store
        .dispatch("ProgramPageStore/getProgramPageMetaList")
        .then((pages) => {
          this.pages = Array.isArray(pages) ? pages : [];
          const stillExists = this.pages.some((page) => page.id === previousSelected);
          this.selectedPageId = stillExists ? previousSelected : this.value?.pageId || null;
        })
        .catch((err) => {
          this.pages = [];
          this.$root.$error(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  watch: {
    value: {
      handler(val) {
        if (val) {
          this.titleFilter = "";
          this.typeFilter = "";
          this.selectedPageId = val.pageId || null;
          this.fetchPages();
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.page-row {
  cursor: pointer;
}
.selected-page {
  background: rgba(0, 0, 0, 0.05);
}
</style>
